import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot} from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ClientService} from '../services/auth/client.service';
import {ClientStateService} from '../services/auth/client-state.service';
import {ClientWithProducts, ShineUserInfoService} from '../services/auth/shine-auth/shine-user-info.service';

@Injectable({
    providedIn: 'root'
})
export class UserManagementAdminGuard implements CanActivate {
    private readonly shineUserInfoService = inject(ShineUserInfoService);
    private readonly clientService = inject(ClientService);
    private readonly router = inject(Router);
    private readonly clientStateService = inject(ClientStateService);

    canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
        return this.shineUserInfoService.getUserRights().pipe(
            map(response => {
                const clients = response?.status === 'success' ? response.data?.clients ?? [] : [];
                const allowed = this.hasAdminAccess(clients);

                if (allowed) {
                    this.clientStateService.setShellLoadFailure(null);
                    return true;
                }

                this.deny();
                return false;
            }),
            catchError(() => {
                this.deny();
                return of(false);
            })
        );
    }

    private hasAdminAccess(clients: ClientWithProducts[]): boolean {
        const selectedClientId = this.clientService.client?.id;
        const candidates = selectedClientId
            ? clients.filter(client => this.matchesSelectedClient(client, selectedClientId))
            : clients;

        return candidates.some(client =>
            (client.moduleAccess ?? [])
                .some(access => (access.access ?? '').trim().toLowerCase() === 'admin')
        );
    }

    private matchesSelectedClient(client: ClientWithProducts, selectedClientId: string): boolean {
        const info = client.client;
        return [info?.id, info?.Domain, info?.Name]
            .filter((value): value is string => typeof value === 'string')
            .some(value => value === selectedClientId);
    }

    private deny(): void {
        this.clientStateService.setBetaBackendUnavailable(false);
        this.clientStateService.setShellLoadFailure('access-denied');
        this.clientStateService.setClientLoading(false, 'rbac-tenant-denial');
        this.router.navigateByUrl('/ai/dashboard').catch(() => undefined);
    }
}
